import React, { useCallback, useMemo } from 'react';
import { ClassNameMap, IconButton, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useIntl } from 'react-intl';
import { useFeatureFlag } from 'modules/common/hooks';
import { useMessages } from 'modules/intl';
import { useExpandCollapseAllState } from '../../hooks/useExpandCollapseAllState';
import { ReactComponent as CollapseAll } from '../Icons/unfold_less.svg';
import { ReactComponent as ExpandAll } from '../Icons/unfold_more.svg';

const useStyles = makeStyles((theme) => ({
  expandCollapse: {
    margin: theme.spacing(0, 0.5),
    padding: theme.spacing(0, 0.5)
  }
}));

export const useExpandCollapse = ({
  id,
  onExpandCollapseAll
}: ExpandCollapseAllButtonProps): {
  expandAllState: boolean;
  onCollapseExpandClick: () => void;
} => {
  const { expandAllState, setExpandCollapseState } = useExpandCollapseAllState(
    id
  );

  const onCollapseExpandClick = useCallback(() => {
    setExpandCollapseState(!expandAllState);

    if (onExpandCollapseAll) {
      onExpandCollapseAll(!expandAllState);
    }
  }, [expandAllState, setExpandCollapseState, onExpandCollapseAll]);

  return useMemo(
    () => ({
      expandAllState: Boolean(expandAllState),
      onCollapseExpandClick
    }),
    [expandAllState, onCollapseExpandClick]
  );
};

type ExpandCollapseAllButtonProps = {
  id: string;
  onExpandCollapseAll?: (state: boolean) => void;
  overrideClass?: ClassNameMap;
  fullhoursMinuteDuration?: string;
};

const message = ['collapseAll', 'expandAll'];

export const ExpandCollapseAllButton: React.FC<ExpandCollapseAllButtonProps> = ({
  id,
  onExpandCollapseAll,
  overrideClass,
  fullhoursMinuteDuration
}: ExpandCollapseAllButtonProps) => {
  const classes = useStyles({ classes: overrideClass });
  const { formatMessage } = useIntl();
  const { expandAllState, onCollapseExpandClick } = useExpandCollapse({
    id,
    onExpandCollapseAll
  });
  const icon = useMemo(
    () =>
      expandAllState ? (
        <CollapseAll data-testid="collapse-all" />
      ) : (
        <ExpandAll data-testid="expand-all" />
      ),
    [expandAllState]
  );

  const messageLabel = useMessages(message);

  const tooltipTitle = useMemo(
    () =>
      expandAllState ? messageLabel['collapseAll'] : messageLabel['expandAll'],
    [expandAllState, messageLabel]
  );
  const ariaLabelMsg = useMemo(
    () =>
      expandAllState
        ? formatMessage(
            { id: 'collapseAllwithDuration' },
            { duration: fullhoursMinuteDuration }
          )
        : formatMessage(
            { id: 'expandAllwithDuration' },
            { duration: fullhoursMinuteDuration }
          ),
    [expandAllState, formatMessage, fullhoursMinuteDuration]
  );
  const isTiwScreenReaderEnabled = useFeatureFlag('isTiwScreenReaderEnabled');
  const isTiAutoAcceptUserSettingsEnabled = useFeatureFlag(
    'isTiAutoAcceptUserSettingsEnabled'
  );

  return (
    <Tooltip title={tooltipTitle}>
      <IconButton
        data-testid="expand-collpase-all-btn"
        className={classes.expandCollapse}
        onClick={onCollapseExpandClick}
        size="large"
        aria-label={
          isTiwScreenReaderEnabled
            ? isTiAutoAcceptUserSettingsEnabled && fullhoursMinuteDuration
              ? ariaLabelMsg
              : tooltipTitle
            : undefined
        }
      >
        {icon}
      </IconButton>
    </Tooltip>
  );
};
